// common/features/competition/wizard/types.ts

export type CreationMethod = 'new' | 'clone'

export type CompetitionType =
  | 'season'
  | 'playoffs_simple'
  | 'playoffs_enhanced'
  | 'tournament'

/** True when the selection is one of the two playoff cards. */
export const isPlayoffsType = (
  v: CompetitionType | null | undefined
): boolean => v === 'playoffs_simple' || v === 'playoffs_enhanced'

/** Competition Type options shown on the clone form (Step 1.3). */
export type CloneCompetitionType = CompetitionType

/**
 * Clone Teams options (mirrors backend `include_teams` enum):
 * - `none`  — schedule tree only, no teams
 * - `empty` — clone teams without rosters
 * - `full`  — clone teams + rosters
 */
export type CloneTeamsMode = 'none' | 'empty' | 'full'

export type StructureType = 'single' | 'multiple' | 'conferences'

export type JoinType = 'registration' | 'manual'

export type DivisionDraft = {
  /** Client-side UUID. Never sent to the backend. Used for dnd-kit keys and edit/delete targeting. */
  clientId: string
  name: string
  order: number
}

export type ConferenceDraft = {
  clientId: string
  name: string
  order: number
  divisions: DivisionDraft[]
  /** UI-only — controls whether the row is expanded. Retained across back-nav. */
  isExpanded: boolean
}

export type WizardCloneSource = {
  sourceScheduleId: string | null
  /** Display name of the picked source competition — for summary rendering. */
  sourceName: string | null
  name: string
  startsAt: string | null
  endsAt: string | null
  competitionType: CloneCompetitionType | null
  /** Derived from competitionType: true when 'playoffs_enhanced' or 'tournament', false otherwise. */
  isTournament: boolean
  isRegistrationSchedule: boolean
  cloneRegistrationSettings: boolean
  cloneTeams: CloneTeamsMode
}

export type WizardInfo = {
  sportId: string | null
  /** Sport name (e.g. 'hockey', 'soccer') — sent as `sport_type` in the create payload. */
  sportType: string | null
  genericSportId: string | null
  name: string
  startsAt: string | null
  endsAt: string | null
  /**
   * Contact email shown on the public competition page.
   * At least one of `email` / `website` must be filled (enforced in Yup).
   */
  email: string
  /** Public competition URL. At least one of `email` / `website` is required. */
  website: string
}

export type WizardFormValues = {
  creationMethod: CreationMethod | null
  cloneSource: WizardCloneSource
  info: WizardInfo
  competitionType: CompetitionType | null
  /** True for Tournament card; false for everything else. */
  isTournament: boolean
  structureType: StructureType | null
  divisions: DivisionDraft[]
  conferences: ConferenceDraft[]
  joinType: JoinType | null
}

/**
 * Wire competition_type for the create endpoint. Enhanced Playoffs always
 * routes through clone, but Season / Simple Playoffs / Tournament can land
 * here when the user skips picking a clone source on Step 3.
 */
export type CreateCompetitionType = 'season' | 'playoffs_simple' | 'tournament'

export type CreateCompetitionRequest = {
  name: string
  abbreviation: string
  sport_id: string
  sport_type: string
  generic_sport_id?: string | null
  starts_at: string
  ends_at: string
  /** Optional — sent only when filled. At least one of these two is required by the wizard. */
  email?: string
  /** Optional — sent only when filled. At least one of these two is required by the wizard. */
  website?: string
  competition_type: CreateCompetitionType
  is_tournament: boolean
  is_registration_on: boolean
  registration_type: JoinType
  /** Mirrors the user's Step 4 (Structure) selection. */
  type: StructureType
  divisions?: Array<{ name: string; order: number }>
  conferences?: Array<{
    name: string
    order: number
    divisions: Array<{ name: string; order: number }>
  }>
}

export type CloneCompetitionRequest = {
  name: string
  starts_at: string
  ends_at: string
  competition_type: CloneCompetitionType
  is_tournament: boolean
  is_registration_schedule: boolean
  clone_registration_settings: boolean
  include_teams: CloneTeamsMode
}
